/**
 * GARCHSequenceService.ts
 *
 * Centralized service for GARCH sequence generation and caching
 * Eliminates duplication between BaseSimulationEngine and SharedRateGenerator
 */

import { SteuerParameter } from '@/types';
import { garchSequenceCache } from './CacheManager';
import { GARCHUtils } from '../utils/garchModeling';

export interface GARCHRateOptions {
  inflationOption: string;
  investmentReturnOption: string;
  tagesgeldOption: string;
  zinstopfOption?: string;
}

/**
 * Centralized GARCH sequence generation service
 * Provides consistent caching and error handling for all GARCH operations
 */
export class GARCHSequenceService {
  /**
   * Generate GARCH rate sequence with caching
   * @param options - Rate calculation options
   * @param years - Number of years to generate
   * @param seed - Seed for reproducible results
   * @param cachePrefix - Prefix for cache key (e.g., 'static', 'engine')
   * @param iteration - Optional iteration number for cache key
   * @returns Array of GARCH rates or undefined if generation fails
   */
  static async generateGARCHSequence(
    options: GARCHRateOptions,
    years: number,
    seed: number,
    cachePrefix: string = 'garch',
    iteration: number = 0,
    steuerParameter?: SteuerParameter
  ): Promise<number[] | undefined> {
    // Check if this is a GARCH option
    if (!GARCHUtils.isGARCHOption(options.investmentReturnOption)) {
      return undefined;
    }

    try {
      // FIXED: Include iteration in cache key for Monte Carlo to ensure unique sequences
      // FIXED (Off-by-one/Iteration 0): The former check "iteration > 0" wrongly treated
      // iteration 0 as Einzelsimulation, giving it a non-deterministic, uncached sequence.
      // The presence of steuerParameter marks a deterministic (Monte Carlo/engine) call,
      // so iteration 0 is now cached and deterministic exactly like iterations 1..N.
      let cacheKey: string;
      if (steuerParameter !== undefined) {
        // For Monte Carlo: include iteration number to ensure each iteration gets unique sequence
        cacheKey = `${this.generateCacheKey(steuerParameter, options, years, cachePrefix)}-iter-${iteration}`;
      } else {
        // For Einzelsimulation: use old method with randomness (no caching desired)
        cacheKey = `${cachePrefix}-sequence-${years}-${seed}-${iteration}`;
      }

      // Check cache first
      const cachedRates = garchSequenceCache.get(cacheKey);
      if (cachedRates) {
        return [...cachedRates]; // Return copy to avoid mutation
      }

      // Generate new GARCH sequence
      const { RateCalculationService } = await import('./RateCalculationService');
      const garchRates = RateCalculationService.generateGARCHRateSequence(
        options,
        years,
        'world',
        seed
      );

      // Cache the results (only for Monte Carlo to avoid memory bloat in Einzelsimulation)
      // Iteration 0 is cached as well (see cache key logic above).
      if (garchRates && garchRates.length > 0 && steuerParameter !== undefined) {
        garchSequenceCache.set(cacheKey, [...garchRates]);
      }

      return garchRates;
    } catch (error) {
      // Failed to generate GARCH sequence, falling back to standard calculation
      return undefined;
    }
  }

  /**
   * Generate consistent seed for GARCH generation
   * @param steuerParameter - Tax parameters
   * @param options - Rate options
   * @param iteration - Iteration number
   * @returns Consistent seed value
   */
  static generateConsistentSeed(
    steuerParameter: SteuerParameter,
    options: GARCHRateOptions,
    iteration?: number
  ): number {
    // FIXED (Iteration 0): The former check "iteration > 0" wrongly treated iteration 0
    // as Einzelsimulation and gave it a Math.random() seed instead of the deterministic
    // iteration seed. Explicitly passed iterations (including 0) now always use purely
    // deterministic seeding; only calls WITHOUT an iteration (Einzelsimulation) get
    // controlled randomness.
    let randomComponent: number;

    if (iteration !== undefined) {
      // Monte Carlo: Deterministic based on iteration only (iteration 0 included)
      randomComponent = iteration * 1000;
    } else {
      // Einzelsimulation: Use a more controlled random component
      // Still random but avoid Date.now() which changes between calls
      randomComponent = Math.floor(Math.random() * 1000000);
    }

    // Create a consistent seed based on parameters
    // FAIR-COMPARISON-FIX: portfolioEntnahmeRate ist BEWUSST NICHT Teil des Seeds.
    // Die GARCH-Sequenz modelliert Marktrenditen (generateGARCHRateSequence haengt nicht
    // von der Entnahmerate ab). Waere die Entnahmerate im Seed, erhielte dieselbe iteration
    // je Entnahmerate ein ANDERES Marktszenario -> Vergleiche verschiedener Entnahmeraten
    // liefen auf verschiedenen Marktpfaden. So erzeugt dieselbe iteration denselben
    // Marktpfad, unabhaengig von der Entnahmerate.
    const paramString = JSON.stringify({
      vorhandenesKapital: steuerParameter.vorhandenesKapital,
      retirementAge: steuerParameter.retirementAge,
      berechnungBisLebensalter: steuerParameter.berechnungBisLebensalter,
      inflationOption: options.inflationOption,
      investmentReturnOption: options.investmentReturnOption,
      tagesgeldOption: options.tagesgeldOption,
      zinstopfOption: options.zinstopfOption,
      iteration,
      randomComponent, // Deterministic for Monte Carlo, random for Einzelsimulation
    });

    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < paramString.length; i++) {
      const char = paramString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash);
  }

  /**
   * Generate a base cache key for GARCH sequences
   * This key will be combined with iteration number for Monte Carlo uniqueness
   */
  static generateCacheKey(
    steuerParameter: SteuerParameter,
    options: GARCHRateOptions,
    years: number,
    cachePrefix: string = 'garch'
  ): string {
    // Create base cache key for simulation parameters (iteration will be added separately)
    // FAIR-COMPARISON-FIX: portfolioEntnahmeRate ist hier BEWUSST NICHT im Cache-Key.
    // Der Key adressiert die (von der Entnahmerate unabhaengige) GARCH-Marktsequenz und
    // muss daher konsistent zum Seed (siehe generateConsistentSeed) ebenfalls
    // entnahmeraten-unabhaengig sein. Dieselbe Marktsequenz wird so ueber verschiedene
    // Entnahmeraten korrekt wiederverwendet statt redundant neu generiert.
    const paramString = JSON.stringify({
      vorhandenesKapital: steuerParameter.vorhandenesKapital,
      retirementAge: steuerParameter.retirementAge,
      berechnungBisLebensalter: steuerParameter.berechnungBisLebensalter,
      inflationOption: options.inflationOption,
      investmentReturnOption: options.investmentReturnOption,
      tagesgeldOption: options.tagesgeldOption,
      zinstopfOption: options.zinstopfOption,
      years
      // Note: Iteration number is added separately in generateGARCHSequence for uniqueness
    });

    // Simple hash function to create short cache key
    let hash = 0;
    for (let i = 0; i < paramString.length; i++) {
      const char = paramString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return `${cachePrefix}-${Math.abs(hash)}`;
  }

  /**
   * Check if GARCH options are being used
   * @param options - Rate options to check
   * @returns True if GARCH options are enabled
   */
  static isUsingGARCH(options: GARCHRateOptions): boolean {
    return GARCHUtils.isGARCHOption(options.investmentReturnOption);
  }

  /**
   * Clear GARCH cache (useful for testing)
   */
  static clearCache(): void {
    garchSequenceCache.clear();
  }
}
